/**
 * Candle Profile Plugin for LightweightCharts v5 (Planning chart)
 *
 * Renders, for EACH 30m candle, its own mini volume profile (bars extending
 * RIGHT) and its own TPO / time-price profile (bars extending LEFT), each with
 * its own POC + 40% core value area + 70% value area — the same look as the
 * session profiles, but per candle.
 *
 * Data: GET /api/indicators/candle-profiles (one fetch per visible day) plus
 * live updates over the unified SSE 'candle-profiles' channel (forming candle +
 * finalized candles). Profiles are computed server-side from the raw trade tape.
 */

import { CONFIG } from '../config';
import { authFetch } from '../auth/authFetch';
import { unifiedSSE } from '../hooks/useUnifiedSSE';

const COLORS = {
  poc: 'rgba(251, 191, 36, 0.65)',        // gold
  core: 'rgba(251, 146, 60, 0.55)',        // orange (40% core)
  va: 'rgba(38, 166, 154, 0.45)',          // teal (70% value area)
  outside: 'rgba(107, 114, 128, 0.30)',    // grey
  outline: 'rgba(255, 255, 255, 0.12)',
};

const CANDLE_SECONDS = 1800;          // 30m
const PROFILE_WIDTH_FRACTION = 0.45;  // fraction of a candle's slot per side (VP right / TPO left)
const FALLBACK_WIDTH_PX = 40;         // last resort when neither neighbour's coordinate resolves

function classify(price, p, tick) {
  // p = {poc, vah, val, cvah, cval}; returns a COLORS entry
  const tol = tick / 2;
  if (Math.abs(price - p.poc) < tol) return COLORS.poc;
  if (p.cvah && p.cval && price >= p.cval - tol && price <= p.cvah + tol) return COLORS.core;
  if (price >= p.val - tol && price <= p.vah + tol) return COLORS.va;
  return COLORS.outside;
}

class CandleProfileRenderer {
  constructor(store, series, chart, options = {}) {
    this.store = store;
    this.series = series;
    this.chart = chart;
    this.options = options;
  }

  draw(target) {
    const { series, chart } = this;
    if (!series || !chart) return;
    const byDate = this.store.candlesByDate;
    if (!byDate || byDate.size === 0) return;

    target.useBitmapCoordinateSpace((scope) => {
      const ctx = scope.context;
      const { horizontalPixelRatio: hpr, verticalPixelRatio: vpr } = scope;
      const timeScale = chart.timeScale();
      const showVol = this.store.showVolume;
      const showTpo = this.store.showTpo;

      for (const candleMap of byDate.values()) {
        for (const c of candleMap.values()) {
          this._drawOne(ctx, c, timeScale, hpr, vpr, showVol, showTpo);
        }
      }
    });
  }

  _barHeight(levels, tick, vpr) {
    for (let i = 0; i < levels.length; i++) {
      const yA = this.series.priceToCoordinate(levels[i] + tick / 2);
      const yB = this.series.priceToCoordinate(levels[i] - tick / 2);
      if (yA !== null && yB !== null) {
        return Math.max(Math.abs((yB - yA) * vpr), 1);
      }
    }
    return 1;
  }

  _drawOne(ctx, c, timeScale, hpr, vpr, showVol, showTpo) {
    const anchor = timeScale.timeToCoordinate(c.candle_ts);
    if (anchor === null) return;

    const frac = this.store.widthFraction ?? PROFILE_WIDTH_FRACTION;
    const next = timeScale.timeToCoordinate(c.candle_ts + CANDLE_SECONDS);
    let widthLogical = FALLBACK_WIDTH_PX * frac;
    if (next !== null && next > anchor) {
      widthLogical = (next - anchor) * frac;
    } else {
      // Developing (right-most) candle: its next slot is not on the time scale
      // yet, so timeToCoordinate(next) is null. Candles are evenly spaced, so
      // use the PREVIOUS slot width to scale it identically to historic candles
      // instead of the fixed-px fallback (which over-widens it).
      const prev = timeScale.timeToCoordinate(c.candle_ts - CANDLE_SECONDS);
      if (prev !== null && prev < anchor) {
        widthLogical = (anchor - prev) * frac;
      }
    }
    const anchorX = anchor * hpr;
    const maxW = widthLogical * hpr;
    const tick = c.tick_size || 0.25;

    if (showVol && c.vp && c.vp.price_levels && c.vp.price_levels.length) {
      this._drawSide(ctx, c.vp, tick, anchorX, maxW, vpr, +1, c.vp.volumes);
    }
    if (showTpo && c.tpo && c.tpo.price_levels && c.tpo.price_levels.length) {
      this._drawSide(ctx, c.tpo, tick, anchorX, maxW, vpr, -1, c.tpo.counts);
    }
  }

  // dir +1 => extend RIGHT (volume), dir -1 => extend LEFT (tpo)
  _drawSide(ctx, prof, tick, anchorX, maxW, vpr, dir, weights) {
    const levels = prof.price_levels;
    const barHeight = this._barHeight(levels, tick, vpr);
    const half = barHeight / 2;

    for (let i = 0; i < levels.length; i++) {
      const price = levels[i];
      const w = ((weights[i] || 0) / 100) * maxW;
      if (w <= 0) continue;
      const priceY = this.series.priceToCoordinate(price);
      if (priceY === null) continue;
      const y = priceY * vpr - half;
      const x = dir > 0 ? anchorX : anchorX - w;

      ctx.fillStyle = classify(price, prof, tick);
      ctx.fillRect(x, y, w, barHeight);
      ctx.strokeStyle = COLORS.outline;
      ctx.lineWidth = 0.5;
      ctx.strokeRect(x, y, w, barHeight);
    }
  }
}

class CandleProfilePrimitiveView {
  constructor(store, series, chart, options) {
    this._renderer = new CandleProfileRenderer(store, series, chart, options);
  }
  zOrder() { return 'top'; }   // draw above the (transparent on planning) native body
  renderer() { return this._renderer; }
}

class CandleProfilePrimitive {
  constructor(store, series, chart, options) {
    this._view = new CandleProfilePrimitiveView(store, series, chart, options);
    this._requestUpdate = null;
    this._rafPending = false;
  }
  attached({ requestUpdate }) { this._requestUpdate = requestUpdate; }
  detached() { this._requestUpdate = null; this._rafPending = false; }
  updateAllViews() { return this._view; }
  paneViews() { return [this._view]; }
  scheduleUpdate() {
    if (this._requestUpdate && !this._rafPending) {
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        if (this._requestUpdate) this._requestUpdate();
      });
    }
  }
}

export class CandleProfilePlugin {
  constructor(chart, series, chartContainer) {
    this.chart = chart;
    this.series = series;
    this.chartContainer = chartContainer;

    // Shared store the renderer reads each frame.
    this.store = {
      candlesByDate: new Map(),  // dateStr -> Map(candle_ts -> candle profile)
      showVolume: true,
      showTpo: true,
      widthFraction: PROFILE_WIDTH_FRACTION,  // per-side fraction of a candle slot
    };

    this.enabled = false;
    this.sessionType = 'eth';     // full session, so every candle shown gets a profile
    this.timeframe = '30m';
    this.primitive = null;

    this.pendingFetches = new Set();
    this._sseUnsubs = [];
    this.visibleRangeSubscription = null;
    this.visibleRangeDebounceTimer = null;
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────

  async enable() {
    if (this.enabled) return;
    this.enabled = true;
    this.primitive = new CandleProfilePrimitive(this.store, this.series, this.chart, {});
    this.series.attachPrimitive(this.primitive);
    this._subscribeToVisibleRangeChanges();
    await this._fetchVisibleProfiles();
    this._connectSSE();
  }

  disable() {
    this._disconnectSSE();
    this._unsubscribeFromVisibleRangeChanges();
    if (this.primitive) {
      try { this.series.detachPrimitive(this.primitive); } catch (e) { /* ignore */ }
      this.primitive = null;
    }
    this.store.candlesByDate.clear();
    this.pendingFetches.clear();
    this.enabled = false;
  }

  isEnabled() { return this.enabled; }

  setShowVolume(v) { this.store.showVolume = v; this._redraw(); }
  setShowTpo(v) { this.store.showTpo = v; this._redraw(); }
  setWidthFraction(f) { if (f != null) { this.store.widthFraction = f; this._redraw(); } }

  async setSessionType(st) {
    const next = (st || 'rth').toLowerCase();
    if (next === this.sessionType) return;
    this.sessionType = next;
    this.store.candlesByDate.clear();
    this.pendingFetches.clear();
    if (this.enabled) {
      await this._fetchVisibleProfiles();
      this._redraw();
    }
  }

  _redraw() {
    if (this.primitive) this.primitive.scheduleUpdate();
  }

  // Public: (re)fetch profiles for the currently visible days. Safe to call
  // repeatedly — _fetchVisibleProfiles skips days already cached or in flight.
  // Called by the chart once candle data is applied so the initial fetch isn't
  // lost to an empty visible range on first load.
  async refresh() {
    if (this.enabled) await this._fetchVisibleProfiles();
  }

  // ── Visible-range on-demand fetching ───────────────────────────────────

  _getVisibleDates() {
    if (!this.chart) return [];
    const tr = this.chart.timeScale().getVisibleRange();
    if (!tr) return [];
    const start = new Date(tr.from * 1000);
    const end = new Date(tr.to * 1000);
    const days = [];
    const cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
    const last = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
    while (cur <= last) {
      const dow = cur.getUTCDay();
      if (dow !== 0 && dow !== 6) days.push(cur.toISOString().split('T')[0]);
      cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return days;
  }

  async _fetchVisibleProfiles() {
    if (!this.enabled) return;
    const days = this._getVisibleDates();
    const tasks = days.filter(
      (d) => !this.store.candlesByDate.has(d) && !this.pendingFetches.has(d)
    );
    if (tasks.length === 0) return;
    tasks.forEach((d) => this.pendingFetches.add(d));

    await Promise.all(tasks.map((d) => this._fetchDay(d)));
    this._redraw();
  }

  async _fetchDay(dateStr) {
    try {
      const url = `${CONFIG.apiUrl}/api/indicators/candle-profiles`
        + `?symbol=ES&date=${dateStr}&session_type=${this.sessionType}&timeframe=${this.timeframe}`;
      const resp = await authFetch(url);
      if (!resp.ok) return;
      const data = await resp.json();
      const candles = data.candles || [];
      if (candles.length) {
        this._setDay(dateStr, candles);
      }
    } catch (e) {
      console.warn('[CandleProfile] fetch failed', dateStr, e?.message);
    } finally {
      this.pendingFetches.delete(dateStr);
    }
  }

  _setDay(dateStr, candles) {
    const m = new Map();
    for (const c of candles) m.set(c.candle_ts, c);
    this.store.candlesByDate.set(dateStr, m);
  }

  _upsertCandle(dateStr, candle) {
    if (!candle || candle.candle_ts == null) return;
    let m = this.store.candlesByDate.get(dateStr);
    if (!m) { m = new Map(); this.store.candlesByDate.set(dateStr, m); }
    m.set(candle.candle_ts, candle);
  }

  _subscribeToVisibleRangeChanges() {
    if (!this.chart || this.visibleRangeSubscription) return;
    this.visibleRangeSubscription = this.chart.timeScale().subscribeVisibleLogicalRangeChange(() => {
      if (this.visibleRangeDebounceTimer) clearTimeout(this.visibleRangeDebounceTimer);
      this.visibleRangeDebounceTimer = setTimeout(() => this._fetchVisibleProfiles(), 300);
    });
  }

  _unsubscribeFromVisibleRangeChanges() {
    if (this.visibleRangeSubscription && this.chart) {
      this.chart.timeScale().unsubscribeVisibleLogicalRangeChange(this.visibleRangeSubscription);
      this.visibleRangeSubscription = null;
    }
    if (this.visibleRangeDebounceTimer) {
      clearTimeout(this.visibleRangeDebounceTimer);
      this.visibleRangeDebounceTimer = null;
    }
  }

  // ── Live SSE ───────────────────────────────────────────────────────────

  _connectSSE() {
    this._disconnectSSE();
    this._sseUnsubs.push(
      unifiedSSE.subscribe('candle-profiles', 'candle_profile_snapshot', (data) => {
        if (!this.enabled || !data || data.session_type !== this.sessionType) return;
        // candles already include the live forming candle (server appends it),
        // so a single _setDay handles both closed and forming — same as REST.
        if (Array.isArray(data.candles) && data.candles.length) {
          this._setDay(data.date, data.candles);
        }
        this._redraw();
      })
    );
    this._sseUnsubs.push(
      unifiedSSE.subscribe('candle-profiles', 'candle_profile_update', (data) => {
        if (!this.enabled || !data || !data.candle) return;
        const types = data.session_types || [];
        if (!types.includes(this.sessionType)) return;
        this._upsertCandle(data.date, data.candle);
        this._redraw();
      })
    );
  }

  _disconnectSSE() {
    this._sseUnsubs.forEach((u) => { try { u(); } catch (e) { /* ignore */ } });
    this._sseUnsubs = [];
  }
}

export default CandleProfilePlugin;
